import { Injectable } from '@angular/core';
import {environment} from '../../../environments/environment';
import {ScgHttpWrapper} from '@app/core/injectables/scg-http-wrapper';
import {DeviceService} from '@app/core/services/device.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {Device} from '@app/core/models/device';
import {tap} from 'rxjs/operators';
import { AuthService, UserRole } from '../auth/auth.service';
import {ModaldevComponent, ModalDevData} from '@app/shared/modals/modaldev/modaldev.component';
import {ModalButton, ModalButtons} from '@app/core/models/modal-ref';
import {NGXLogger} from 'ngx-logger';
import {ModalService} from '@app/core/services/modal.service';
import {ModalmissingrightComponent} from '@app/shared/modals/modalmissingright/modalmissingright.component';
import {ScgSharedFactoryService} from '@app/core/services/scg-shared-factory.service';
import type {DeviceIssue} from '@app/parents/services/parents.service';

@Injectable({
  providedIn: 'root'
})
export class ApiNotificationService {
  /** key = deviceId */
  private notificationCountsSubjects: Map<string, BehaviorSubject<NotificationCounts>> = new Map();


  constructor(
    private scgHttp: ScgHttpWrapper,
    private auth: AuthService,
    private scgShared: ScgSharedFactoryService,
    private log: NGXLogger,
    private modalService: ModalService,
    private device: DeviceService
  ) { }

  public init() {
    this.device.onDeviceSwitch.subscribe(newDevice => {
      if (this.auth.role === UserRole.Default || this.auth.role === UserRole.Parents) {
        this.refreshNotificationCounts(newDevice.device);
      }
    });

    this.auth.onLogout.subscribe(() => {
      for (const [, subject] of this.notificationCountsSubjects) {
        subject.complete();
      }
      this.notificationCountsSubjects.clear();
    });
  }

  public getNotificationCounts(deviceId: string): Observable<NotificationCounts> {
    return this.initCountsListener(deviceId).asObservable();
  }

  public getNotifications(deviceId) {
    return this.scgHttp.withDevice(deviceId).get(environment.apiUrl + 'notification');
  }

  public remove(deviceId: string, msgId: string, delDays: number) {
    const postdata = {id : msgId, deleteDays: delDays};
    return this.scgHttp.withDevice(deviceId).post(environment.apiUrl + 'notification/remove', postdata).pipe(
      tap(() => this.refreshCounter(deviceId))
    );
  }

  public refreshCounter(deviceId: string) {
    this.refreshNotificationCounts(deviceId);
  }

  public hasNotifications(deviceId) {
    return this.scgHttp.withDevice(deviceId).
    get<{ notificationCount: number, unlockCount: number, deviceStatus: string, extensionRequest: boolean, hasIssue: boolean; isUpdateAvailable: boolean; alertCount: number; deviceIssues?: DeviceIssue[] }>(environment.apiUrl + 'notification/count');
  }

  // username: CC-Anzeigename, sonst findet das Backend den Verlängerungswunsch nicht (CB ignoriert ihn)
  public getExtensionRequest(deviceId, username?: string) {
    const params = username ? {username} : undefined;
    return this.scgHttp.withDevice(deviceId).get<any>(environment.apiUrl + 'notification/extensionRequest', {params});
  }

  public deleteExtensionRequest(deviceId, user) {
    const postdata = {username : user};
    return this.scgHttp.withDevice(deviceId).post(environment.apiUrl + 'notification/deleteextensionrequest', postdata);
  }

  public restore(deviceId: string) {
    return this.scgHttp.withDevice(deviceId).get(environment.apiUrl + 'notification/restore').pipe(
      tap(() => this.refreshNotificationCounts(deviceId))
    );
  }

  private refreshNotificationCounts(deviceId: string|Device) {
    let device;
    if (deviceId instanceof Device) {
      device = deviceId;
    } else {
      device = this.device.devices.find(d => d.id === deviceId);
    }

    if (!this.notificationCountsSubjects.get(device.id)) {
      // No refresh, this is first pull
      this.initCountsListener(device.id);
      return;
    }

    this.hasNotifications(device.id).subscribe(response => {
      const notificationCountsSubject = this.initCountsListener(device.id);
      // deviceStatus MUST stay in this diff check — see .claude/notes_notification_counts_diff_check.md
      if (notificationCountsSubject.value.notificationCount !== response.notificationCount
        || notificationCountsSubject.value.unlockCount !== response.unlockCount
        || notificationCountsSubject.value.extensionRequest !== response.extensionRequest
        || notificationCountsSubject.value.hasIssue !== response.hasIssue
        || notificationCountsSubject.value.isUpdateAvailable !== response.isUpdateAvailable
        || notificationCountsSubject.value.alertCount !== response.alertCount
        || notificationCountsSubject.value.deviceStatus !== response.deviceStatus
        // Geraete-Problem: Liste kurz, Vergleich als JSON reicht
        || JSON.stringify(notificationCountsSubject.value.deviceIssues || []) !== JSON.stringify(response.deviceIssues || [])) {
        notificationCountsSubject.next({
          notificationCount: response.notificationCount,
          unlockCount: response.unlockCount,
          extensionRequest: response.extensionRequest,
          hasIssue: response.hasIssue,
          isUpdateAvailable: response.isUpdateAvailable,
          alertCount: response.alertCount,
          deviceStatus: response.deviceStatus,
          deviceIssues: response.deviceIssues || []
        });
      }
    });
  }

  private initCountsListener(deviceId: string): BehaviorSubject<NotificationCounts> {
    let notificationCountsSubject = this.notificationCountsSubjects.get(deviceId);

    if (!notificationCountsSubject) {
      notificationCountsSubject = new BehaviorSubject<NotificationCounts>({
        notificationCount: 0,
        unlockCount: 0,
        extensionRequest: false,
        isUpdateAvailable: false,
        hasIssue: false,
        alertCount: 0,
        deviceStatus: '',
        deviceIssues: []
      });
      this.notificationCountsSubjects.set(deviceId, notificationCountsSubject);

      this.hasNotifications(deviceId).subscribe(response => {
        notificationCountsSubject.next({
          notificationCount: response.notificationCount,
          unlockCount: response.unlockCount,
          extensionRequest: response.extensionRequest,
          hasIssue: response.hasIssue,
          isUpdateAvailable: response.isUpdateAvailable,
          alertCount: response.alertCount,
          deviceStatus: response.deviceStatus,
          deviceIssues: response.deviceIssues || []
        });
      });
    }

    return notificationCountsSubject;
  }


}




/**
 * Gibt es fuer das Geraet etwas auf der Warnseite /warnings? Dieselben Quellen wie app-device-warnings:
 * fehlende Rechte (deviceStatus 0 Geraeteadmin, 1 Bedienungshilfe, 3 Overlay, s. .claude/notes_device_status.md),
 * Play-Store-Problem, Manipulations-Alerts, Geraete-Problem.
 */
export function hasDeviceWarnings(counts: NotificationCounts): boolean {
  if (counts == null) return false;
  if (counts.hasIssue === true) return true;
  if (counts.alertCount > 0) return true;
  if ((counts.deviceIssues || []).length > 0) return true;
  const s = counts.deviceStatus;
  if (s == null || s.length < 5) return false;
  return s[0] === '0' || s[1] === '0' || s[3] === '0';
}

export interface NotificationCounts {
  notificationCount: number;
  unlockCount: number;
  extensionRequest: boolean;
  hasIssue: boolean;
  isUpdateAvailable: boolean;
  alertCount: number;
  deviceStatus: string;
  // Geraete-Problem (nur CC): sichtbare Codes {code, since}, leer = keins. Optional: aeltere Backends liefern es nicht.
  deviceIssues?: DeviceIssue[];

}
